import { normalizeRaceClass, type RaceClass } from "./simulation/balance";
import { trackCatalog } from "./simulation/track";
import type { ChampionshipRouteId, CupGrade, CupRaceRecord, CupState } from "./simulation/types";
import { nextRaceVariantSeed, normalizeRaceVariantSeed } from "./simulation/raceVariants";
import { MAX_RACE_CAPACITY } from "./simulation/raceCapacity";
import { KART_NAME_MAX_LENGTH } from "./kartLoadout";

export const CHAMPIONSHIP_POINTS = Object.freeze([15, 12, 10, 8, 6, 4, 2, 1] as const);
export const CHAMPIONSHIP_TRACK_IDS = Object.freeze(trackCatalog.map((track) => track.id));
const DEFAULT_KART_IDS = Object.freeze(CHAMPIONSHIP_POINTS.map((_, index) => `kart-${index}`));
export const DEFAULT_CHAMPIONSHIP_ROUTE_ID: ChampionshipRouteId = "brewers-tour";

export interface ChampionshipRouteDefinition {
  id: ChampionshipRouteId;
  name: string;
  shortName: string;
  tagline: string;
  trackOrder: readonly string[];
  preferredDuelRivalKartId: string;
  roundThemes: readonly string[];
}

export const CHAMPIONSHIP_ROUTES: Readonly<Record<ChampionshipRouteId, ChampionshipRouteDefinition>> = Object.freeze({
  "brewers-tour": Object.freeze({
    id: "brewers-tour",
    name: "BREWER'S GRAND TOUR",
    shortName: "SIX-COURSE CUP",
    tagline: "THE CLASSIC TAPROOM TOUR",
    trackOrder: Object.freeze([...CHAMPIONSHIP_TRACK_IDS]),
    preferredDuelRivalKartId: "kart-1",
    roundThemes: Object.freeze(["OPENING POUR", "CITY PRESSURE", "SUNSET CHARGE", "UNDERGROUND TEST", "COURTYARD SPRINT", "FINAL FRONTIER"]),
  }),
  "last-orders-tour": Object.freeze({
    id: "last-orders-tour",
    name: "LAST ORDERS TOUR",
    shortName: "LAST ORDERS",
    tagline: "DAYLIGHT TO DEEP SPACE",
    trackOrder: Object.freeze(["citrus-courtyard", "malt-mountain", "dustbowl-draft", "keg-catacombs", "neon-harbor", "moon-tap-one"]),
    preferredDuelRivalKartId: "kart-7",
    roundThemes: Object.freeze(["GOLDEN HOUR", "BREWERY BLITZ", "SUNDOWN DRAFT", "CELLAR DESCENT", "NEON LAST CALL", "ONE FINAL ROUND"]),
  }),
});

/**
 * Runtime open-roster cups may carry every entrant seen across their authored
 * route, while a single race still renders at most the supported twenty-kart
 * field size.  Keep this bound derived from the authored routes so adding a
 * longer route cannot silently truncate its ledger.
 */
export const MAX_RUNTIME_CUP_ENTRANTS = MAX_RACE_CAPACITY * Math.max(
  ...Object.values(CHAMPIONSHIP_ROUTES).map((route) => route.trackOrder.length),
);

export function normalizeChampionshipRouteId(value: unknown): ChampionshipRouteId {
  return value === "last-orders-tour" ? "last-orders-tour" : DEFAULT_CHAMPIONSHIP_ROUTE_ID;
}

export function getChampionshipRouteDefinition(value: ChampionshipRouteId | CupState | unknown): ChampionshipRouteDefinition {
  const routeId = typeof value === "object" && value !== null && "routeId" in value ? normalizeChampionshipRouteId((value as CupState).routeId) : normalizeChampionshipRouteId(value);
  return CHAMPIONSHIP_ROUTES[routeId];
}

export function getNextChampionshipRouteId(cup: CupState | undefined): ChampionshipRouteId {
  return normalizeChampionshipRouteId(cup?.routeId) === "brewers-tour" ? "last-orders-tour" : "brewers-tour";
}

export function selectChampionshipDuelRival(
  routeId: ChampionshipRouteId,
  entrants: readonly { id: string; controller?: string }[],
): string | null {
  const eligible = entrants.filter((entry) => entry.controller === "ai" || (entry.controller === undefined && entry.id !== "kart-0"));
  const preferred = getChampionshipRouteDefinition(routeId).preferredDuelRivalKartId;
  return eligible.find((entry) => entry.id === preferred)?.id ?? eligible[0]?.id ?? null;
}

export interface ChampionshipKartResult {
  id: string;
  place: number;
  finishedAt?: number | null;
  name?: string;
}

export interface ChampionshipStanding {
  kartId: string;
  name?: string;
  position: number;
  points: number;
  wins: number;
  finishCounts: number[];
}

export interface ChampionshipAward {
  awarded: boolean;
  reason: "awarded" | "duplicate" | "complete" | "out-of-order" | "invalid-result";
  resultKey: string | null;
  pointsAwarded: Record<string, number>;
  standings: ChampionshipStanding[];
  championKartId: string | null;
  cup: CupState;
}

function finiteNonNegative(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0;
}

function uniqueKartIds(values: readonly string[] | undefined, limit = MAX_RACE_CAPACITY): string[] {
  const unique: string[] = [];
  for (const value of values ?? DEFAULT_KART_IDS) {
    const id = typeof value === "string" ? value.trim() : "";
    if (id && !unique.includes(id) && unique.length < limit) unique.push(id);
  }
  return unique;
}

// Preserve the original eight-place points table. Larger grids receive no
// points below eighth, but every finishing position still breaks tied totals.
function emptyFinishCounts(entrants: number = CHAMPIONSHIP_POINTS.length, openRoster = false): number[] {
  const length = openRoster ? MAX_RACE_CAPACITY : Math.max(CHAMPIONSHIP_POINTS.length, Math.min(MAX_RACE_CAPACITY, entrants));
  return Array.from({ length }, () => 0);
}

interface ChampionshipEntrant {
  id: string;
  name: string;
}

function normalizedEntrant(value: ChampionshipEntrant | undefined): ChampionshipEntrant | null {
  if (!value || typeof value.id !== "string" || typeof value.name !== "string") return null;
  const id = value.id.trim();
  // Keep the same sanitization and twenty-character display bound used by
  // server/browser kart loadouts, but reject an entrant whose name normalizes
  // to empty rather than inventing a fallback identity.
  const name = value.name.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, " ").trim().slice(0, KART_NAME_MAX_LENGTH);
  return id && name ? { id, name } : null;
}

function selectedOpenEntrants(values: readonly ChampionshipEntrant[] | undefined): ChampionshipEntrant[] {
  const selected: ChampionshipEntrant[] = [];
  for (const value of values ?? []) {
    const entrant = normalizedEntrant(value);
    if (entrant && !selected.some((candidate) => candidate.id === entrant.id) && selected.length < MAX_RACE_CAPACITY) selected.push(entrant);
  }
  return selected;
}

export function createChampionshipCup(
  raceClass: RaceClass,
  kartIds?: readonly string[],
  variantSeed = 0,
  routeId: ChampionshipRouteId = DEFAULT_CHAMPIONSHIP_ROUTE_ID,
  duelRivalKartId?: string | null,
): CupState {
  const entrantOrder = uniqueKartIds(kartIds);
  const route = getChampionshipRouteDefinition(routeId);
  const pointsByKart = Object.fromEntries(entrantOrder.map((id) => [id, 0]));
  const winsByKart = Object.fromEntries(entrantOrder.map((id) => [id, 0]));
  const finishCountsByKart = Object.fromEntries(entrantOrder.map((id) => [id, emptyFinishCounts(entrantOrder.length)]));
  const gradeByKart = Object.fromEntries(entrantOrder.map((id) => [id, "none" as CupGrade]));
  return {
    raceIndex: 0,
    totalRaces: route.trackOrder.length,
    pointsByKart,
    completed: false,
    raceClass: normalizeRaceClass(raceClass),
    trackOrder: [...route.trackOrder],
    entrantOrder,
    winsByKart,
    finishCountsByKart,
    awardedRaceKeys: {},
    resultsByTrack: {},
    championKartId: null,
    gradeByKart,
    variantSeed: normalizeRaceVariantSeed(variantSeed),
    routeId: route.id,
    duelRivalKartId: duelRivalKartId === null ? null : entrantOrder.includes(duelRivalKartId ?? route.preferredDuelRivalKartId) ? duelRivalKartId ?? route.preferredDuelRivalKartId : null,
  };
}

/**
 * Create the opt-in runtime cup variant.  The initial grid is bounded to one
 * race's twenty slots; later rounds may add new identities through
 * registerOpenChampionshipEntrants without pruning this cup's history.
 */
export function createOpenChampionshipCup(
  raceClass: RaceClass,
  entrants: readonly ChampionshipEntrant[],
  variantSeed = 0,
  routeId: ChampionshipRouteId = DEFAULT_CHAMPIONSHIP_ROUTE_ID,
  duelRivalKartId?: string | null,
): CupState {
  const selected = selectedOpenEntrants(entrants);
  const cup = createChampionshipCup(raceClass, selected.map((entrant) => entrant.id), variantSeed, routeId, duelRivalKartId);
  cup.openRoster = true;
  cup.nameByKart = Object.fromEntries(selected.map((entrant) => [entrant.id, entrant.name]));
  cup.finishCountsByKart = Object.fromEntries(selected.map((entrant) => [entrant.id, emptyFinishCounts(MAX_RACE_CAPACITY, true)]));
  return cup;
}

/**
 * Add the current open-roster selection atomically. Existing identities may
 * return in a later round; their points, countback and canonical display name
 * remain attached to the same ID while absent identities stay in the ledger.
 */
export function registerOpenChampionshipEntrants(cup: CupState, entrants: readonly ChampionshipEntrant[]): boolean {
  if (cup.openRoster !== true || cup.completed || entrants.length > MAX_RACE_CAPACITY) return false;

  const selected = entrants.map(normalizedEntrant);
  if (selected.some((entrant): entrant is null => entrant === null)) return false;
  const normalizedSelected = selected as ChampionshipEntrant[];
  const selectedIds = normalizedSelected.map((entrant) => entrant.id);
  if (new Set(selectedIds).size !== selectedIds.length) return false;

  const currentIds = Array.isArray(cup.entrantOrder) ? cup.entrantOrder : Object.keys(cup.pointsByKart ?? {});
  const existingIds = uniqueKartIds(currentIds, MAX_RUNTIME_CUP_ENTRANTS);
  const nextIds = [...existingIds];
  for (const id of selectedIds) if (!nextIds.includes(id)) nextIds.push(id);
  if (nextIds.length > MAX_RUNTIME_CUP_ENTRANTS) return false;

  // Stage through cloneCup so no map/array is mutated before every validation
  // above has succeeded. Assignment below is the sole commit point.
  const staged = cloneCup(cup);
  const names = { ...(staged.nameByKart ?? {}) };
  for (const entrant of normalizedSelected) if (!names[entrant.id]) names[entrant.id] = entrant.name;
  staged.entrantOrder = nextIds;
  staged.nameByKart = names;
  staged.openRoster = true;
  staged.pointsByKart = Object.fromEntries(nextIds.map((id) => [id, finiteNonNegative(staged.pointsByKart[id])]));
  staged.winsByKart = Object.fromEntries(nextIds.map((id) => [id, finiteNonNegative(staged.winsByKart?.[id])]));
  staged.finishCountsByKart = Object.fromEntries(nextIds.map((id) => [id, emptyFinishCounts(MAX_RACE_CAPACITY, true).map((_, index) => finiteNonNegative(staged.finishCountsByKart?.[id]?.[index]))]));
  staged.gradeByKart = Object.fromEntries(nextIds.map((id) => [id, staged.gradeByKart?.[id] ?? "none"]));
  staged.duelRivalKartId = staged.duelRivalKartId !== null && typeof staged.duelRivalKartId === "string" && nextIds.includes(staged.duelRivalKartId)
    ? staged.duelRivalKartId
    : null;
  Object.assign(cup, staged);
  return true;
}

export function cloneCup(source: CupState): CupState {
  const route = getChampionshipRouteDefinition(source);
  const openRoster = source.openRoster === true;
  const entrantLimit = openRoster ? MAX_RUNTIME_CUP_ENTRANTS : MAX_RACE_CAPACITY;
  const entrantOrder = uniqueKartIds(source.entrantOrder?.length ? source.entrantOrder : Object.keys(source.pointsByKart ?? {}), entrantLimit);
  const pointsByKart: Record<string, number> = {};
  const winsByKart: Record<string, number> = {};
  const finishCountsByKart: Record<string, number[]> = {};
  for (const id of entrantOrder) {
    pointsByKart[id] = finiteNonNegative(source.pointsByKart?.[id]);
    winsByKart[id] = finiteNonNegative(source.winsByKart?.[id]);
    const counts = source.finishCountsByKart?.[id];
    finishCountsByKart[id] = emptyFinishCounts(entrantOrder.length, openRoster).map((_, index) => finiteNonNegative(counts?.[index]));
  }
  const nameByKart: Record<string, string> = {};
  for (const id of entrantOrder) {
    const name = source.nameByKart?.[id];
    const normalizedName = typeof name === "string"
      ? name.replace(/[^a-zA-Z0-9 _-]/g, "").replace(/\s+/g, " ").trim().slice(0, KART_NAME_MAX_LENGTH)
      : "";
    if (normalizedName) nameByKart[id] = normalizedName;
  }
  const resultsByTrack: Record<string, CupRaceRecord> = {};
  for (const trackId of route.trackOrder) {
    const record = source.resultsByTrack?.[trackId];
    if (!record || typeof record.resultId !== "string" || typeof record.resultKey !== "string" || !Array.isArray(record.orderedKartIds)) continue;
    resultsByTrack[trackId] = {
      trackId,
      resultId: record.resultId,
      resultKey: record.resultKey,
      orderedKartIds: record.orderedKartIds.filter((id): id is string => typeof id === "string"),
      pointsByKart: Object.fromEntries(Object.entries(record.pointsByKart ?? {}).map(([id, points]) => [id, finiteNonNegative(points)])),
    };
  }
  const awardedRaceKeys = Object.fromEntries(Object.keys(source.awardedRaceKeys ?? {}).filter((key) => typeof key === "string").map((key) => [key, true as const]));
  const raceIndex = Math.max(0, Math.min(route.trackOrder.length, Math.floor(finiteNonNegative(source.raceIndex))));
  const completed = Boolean(source.completed) || raceIndex >= route.trackOrder.length;
  const gradeByKart: Record<string, CupGrade> = Object.fromEntries(entrantOrder.map((id): [string, CupGrade] => {
    const value = source.gradeByKart?.[id];
    return [id, value === "bronze" || value === "silver" || value === "gold" || value === "three-star" ? value : "none"];
  }));
  return {
    raceIndex,
    totalRaces: route.trackOrder.length,
    pointsByKart,
    completed,
    ...(openRoster ? { openRoster: true as const, ...(Object.keys(nameByKart).length ? { nameByKart } : {}) } : {}),
    raceClass: normalizeRaceClass(source.raceClass),
    trackOrder: [...route.trackOrder],
    entrantOrder,
    winsByKart,
    finishCountsByKart,
    awardedRaceKeys,
    resultsByTrack,
    championKartId: typeof source.championKartId === "string" && entrantOrder.includes(source.championKartId) ? source.championKartId : null,
    gradeByKart,
    variantSeed: normalizeRaceVariantSeed(source.variantSeed),
    routeId: route.id,
    duelRivalKartId: source.duelRivalKartId === null ? null : typeof source.duelRivalKartId === "string" && entrantOrder.includes(source.duelRivalKartId)
      ? source.duelRivalKartId
      : entrantOrder.includes(route.preferredDuelRivalKartId) ? route.preferredDuelRivalKartId : null,
  };
}

export function getChampionshipRaceVariantSeed(cup: CupState): number {
  return (normalizeRaceVariantSeed(cup.variantSeed) + Math.max(0, Math.floor(finiteNonNegative(cup.raceIndex)))) >>> 0;
}

export function getNextChampionshipVariantSeed(cup: CupState | undefined): number {
  return nextRaceVariantSeed(cup?.variantSeed);
}

function normalizeCupInPlace(cup: CupState): CupState {
  Object.assign(cup, cloneCup(cup));
  return cup;
}

function compareIds(a: string, b: string): number { return a < b ? -1 : a > b ? 1 : 0; }

export function getChampionshipStandings(cup: CupState, karts?: readonly Pick<ChampionshipKartResult, "id" | "name">[]): ChampionshipStanding[] {
  const normalized = cloneCup(cup);
  const entrantOrder = normalized.entrantOrder!;
  const names = new Map(Object.entries(normalized.nameByKart ?? {}));
  for (const kart of karts ?? []) if (kart.name) names.set(kart.id, kart.name);
  const sorted = entrantOrder.map((kartId) => ({
    kartId,
    ...(names.get(kartId) ? { name: names.get(kartId) } : {}),
    position: 0,
    points: normalized.pointsByKart[kartId] ?? 0,
    wins: normalized.winsByKart?.[kartId] ?? 0,
    finishCounts: [...(normalized.finishCountsByKart?.[kartId] ?? emptyFinishCounts(entrantOrder.length))],
  })).sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.wins !== a.wins) return b.wins - a.wins;
    for (let place = 0; place < Math.max(CHAMPIONSHIP_POINTS.length, Math.min(MAX_RACE_CAPACITY, entrantOrder.length)); place += 1) {
      if (b.finishCounts[place] !== a.finishCounts[place]) return b.finishCounts[place] - a.finishCounts[place];
    }
    const order = entrantOrder.indexOf(a.kartId) - entrantOrder.indexOf(b.kartId);
    return order || compareIds(a.kartId, b.kartId);
  });
  sorted.forEach((standing, index) => { standing.position = index + 1; });
  return sorted;
}

export function getNextChampionshipTrackId(cup: CupState): string | null {
  const normalized = cloneCup(cup);
  return normalized.completed ? null : normalized.trackOrder?.[normalized.raceIndex] ?? null;
}

export function getCupGrade(cup: CupState, kartId: string): CupGrade {
  const normalized = cloneCup(cup);
  if (!normalized.completed || !normalized.entrantOrder!.includes(kartId)) return "none";
  const standings = getChampionshipStandings(normalized);
  const standing = standings.find((entry) => entry.kartId === kartId);
  if (!standing) return "none";
  const champion = normalized.championKartId ?? standings[0]?.kartId ?? null;
  if (kartId === champion && standing.wins >= normalized.totalRaces && standing.points >= normalized.totalRaces * CHAMPIONSHIP_POINTS[0]) return "three-star";
  if (kartId === champion) return "gold";
  if (standing.position <= 3) return "silver";
  return "bronze";
}

function resultIdentity(trackId: string, ordered: readonly ChampionshipKartResult[], supplied?: string): string {
  const explicit = supplied?.trim();
  if (explicit) return explicit;
  return ordered.map((kart) => {
    const finish = typeof kart.finishedAt === "number" && Number.isFinite(kart.finishedAt) ? kart.finishedAt.toFixed(3) : "pending";
    return `${kart.id}@${kart.place}@${finish}`;
  }).join(",");
}

export function awardChampionshipRace(
  cup: CupState,
  karts: readonly ChampionshipKartResult[],
  trackId: string,
  resultId?: string,
): ChampionshipAward {
  normalizeCupInPlace(cup);
  const standings = () => getChampionshipStandings(cup, karts);
  const failure = (reason: ChampionshipAward["reason"], resultKey: string | null = null): ChampionshipAward => ({
    awarded: false, reason, resultKey, pointsAwarded: {}, standings: standings(), championKartId: cup.championKartId ?? null, cup,
  });
  if (cup.completed) return failure("complete");
  if (cup.resultsByTrack?.[trackId]) return failure("duplicate", cup.resultsByTrack[trackId].resultKey);
  if (trackId !== getNextChampionshipTrackId(cup)) return failure("out-of-order");

  const entrantOrder = cup.entrantOrder!;
  const openRoster = cup.openRoster === true;
  if (karts.length === 0 || (openRoster ? karts.length > MAX_RACE_CAPACITY : karts.length !== entrantOrder.length)) return failure("invalid-result");
  const ordered = [...karts].sort((a, b) => a.place - b.place || finiteNonNegative(a.finishedAt) - finiteNonNegative(b.finishedAt) || compareIds(a.id, b.id));
  const ids = ordered.map((kart) => kart.id);
  const places = ordered.map((kart) => kart.place);
  if (new Set(ids).size !== ids.length || ids.some((id) => !entrantOrder.includes(id))) return failure("invalid-result");
  const expectedEntrants = openRoster ? ordered.length : entrantOrder.length;
  if (places.some((place) => !Number.isInteger(place) || place < 1 || place > expectedEntrants) || new Set(places).size !== places.length) return failure("invalid-result");

  const identity = resultIdentity(trackId, ordered, resultId);
  const resultKey = `${trackId}::${identity}`;
  if (cup.awardedRaceKeys?.[resultKey]) return failure("duplicate", resultKey);
  const pointsAwarded: Record<string, number> = {};
  for (let index = 0; index < ordered.length; index += 1) {
    const id = ordered[index].id; const points = CHAMPIONSHIP_POINTS[index] ?? 0;
    cup.pointsByKart[id] = (cup.pointsByKart[id] ?? 0) + points;
    cup.winsByKart![id] = (cup.winsByKart![id] ?? 0) + (index === 0 ? 1 : 0);
    cup.finishCountsByKart![id] ??= emptyFinishCounts(entrantOrder.length, openRoster);
    cup.finishCountsByKart![id][index] = (cup.finishCountsByKart![id][index] ?? 0) + 1;
    pointsAwarded[id] = points;
  }
  cup.awardedRaceKeys![resultKey] = true;
  cup.resultsByTrack![trackId] = { trackId, resultId: identity, resultKey, orderedKartIds: ids, pointsByKart: { ...pointsAwarded } };
  cup.raceIndex = Math.min(cup.totalRaces, cup.raceIndex + 1);
  cup.completed = cup.raceIndex >= cup.totalRaces;
  const updatedStandings = standings();
  cup.championKartId = cup.completed ? updatedStandings[0]?.kartId ?? null : null;
  cup.gradeByKart = Object.fromEntries(entrantOrder.map((id) => [id, getCupGrade(cup, id)]));
  return { awarded: true, reason: "awarded", resultKey, pointsAwarded, standings: updatedStandings, championKartId: cup.championKartId ?? null, cup };
}
